import { useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { ApplicationCreateDialog } from './ApplicationCreateDialog'
import { OutcomeBadge, ResultBadge, STAGES, useRefreshApplications, type ApplicationCard } from './shared'

const ALL = '__all__'

/**
 * The applications of the organization as a pipeline by stage (spec
 * 2026-10-25-11). Under the `prioritise` effect a column is ordered by
 * verdict then by enquiry age; there is no multi-select, so no bulk refusal.
 */
export function RentalApplicationsPage() {
    const { org } = useParams({ from: '/$org/rental-applications/' })
    const search = useSearch({ from: '/$org/rental-applications/' })
    const navigate = useNavigate()
    const { t } = useTranslation('rentalApplications')
    const canWrite = useCan('rental_applications.write')
    const [creating, setCreating] = useState(false)
    useBreadcrumbs([{ title: t('common:nav.home'), path: `/${org}` }, { title: t('title') }])

    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/rental-applications', {
        params: { path: { orgCode: org }, query: { listing_uid: search.listing } },
    })
    const { data: listings } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/listings', { params: { path: { orgCode: org } } })
    const { data: neutralised } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/screening-policies', {
        params: { path: { orgCode: org }, query: { neutralised: 'true' } },
    })
    const cards = data?.applications ?? []
    const stale = neutralised?.neutralised ?? []

    return (
        <div className="space-y-6" data-testid="rental-applications-page">
            <PageHeader
                icon={SECTION_ICONS['rental-applications']}
                title={t('title')}
                description={t('description')}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" asChild>
                            <Link to="/$org/rental-applications/viewings" params={{ org }} search={{ view: undefined, date: undefined, viewing: undefined }} data-testid="rental-applications-viewings-link">{t('viewings_link')}</Link>
                        </Button>
                        {canWrite && <Button onClick={() => setCreating(true)} data-testid="rental-application-create-button">{t('create.button')}</Button>}
                    </div>
                }
            />

            {stale.length > 0 && (
                <Alert variant="warning" data-testid="rental-applications-neutralised-banner">
                    <AlertTitle>{t('neutralised.title', { count: stale.length })}</AlertTitle>
                    <AlertDescription>
                        <p>{t('neutralised.description')}</p>
                        <ul className="mt-1 list-disc pl-5">
                            {stale.map((p) => (
                                <li key={p.uid} data-testid="rental-applications-neutralised-item">
                                    {p.criterion_code} · {t(`neutralised.reason.${p.neutralised_reason}`, { defaultValue: p.neutralised_reason ?? '' })}
                                    {p.origin_asset_uid && (
                                        <>
                                            {' · '}
                                            <Link to="/$org/assets/$assetUid" params={{ org, assetUid: p.origin_asset_uid }} className="underline">
                                                {p.origin_name}
                                            </Link>
                                        </>
                                    )}
                                    {p.listing_uid && (
                                        <>
                                            {' · '}
                                            <Link to="/$org/listings/$listingUid" params={{ org, listingUid: p.listing_uid }} className="underline">
                                                {t('neutralised.listing')}
                                            </Link>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            <div className="max-w-sm">
                <Label className="mb-1 block text-sm">{t('filter.listing')}</Label>
                <Select
                    value={search.listing ?? ALL}
                    onValueChange={(v) => navigate({ to: '/$org/rental-applications', params: { org }, search: { listing: v === ALL ? undefined : v }, replace: true })}
                >
                    <SelectTrigger data-testid="rental-applications-listing-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL} data-testid="rental-applications-listing-filter-all">{t('filter.all_listings')}</SelectItem>
                        {(listings?.listings ?? []).map((l) => (
                            <SelectItem key={l.uid} value={l.uid} data-testid={`rental-applications-listing-filter-${l.uid}`}>{l.asset_name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {isLoading ? (
                <Skeleton className="h-40 w-full" />
            ) : (
                <div className="flex gap-3 overflow-x-auto pb-2">
                    {!search.listing && (
                        <UnmatchedColumn org={org} cards={cards.filter((c) => !c.listing_uid && !c.purged_at)} listings={listings?.listings ?? []} canWrite={canWrite} />
                    )}
                    {STAGES.map((stage) => {
                        const inStage = cards.filter((c) => c.stage === stage && (c.listing_uid || c.purged_at))
                        return (
                            <Card key={stage} className="w-64 shrink-0" data-testid="rental-applications-stage-column" data-stage={stage}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm">
                                        {t(`stage.${stage}`)} <span className="text-muted-foreground">({inStage.length})</span>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    {inStage.map((c) => (
                                        <Link
                                            key={c.uid}
                                            to="/$org/rental-applications/$applicationUid"
                                            params={{ org, applicationUid: c.uid }}
                                            className="block rounded-md border p-2 text-sm hover:bg-muted/50"
                                            data-testid="rental-application-card"
                                            data-application-uid={c.uid}
                                        >
                                            <div className="font-medium">{c.purged_at ? t('purged.name') : c.lead_name ?? '—'}</div>
                                            <div className="text-xs text-muted-foreground">{c.asset_name}</div>
                                            <div className="mt-1 flex flex-wrap items-center gap-1 text-xs">
                                                <ResultBadge result={c.screening_verdict} testId="rental-application-card-verdict" />
                                                <OutcomeBadge outcome={c.outcome} testId="rental-application-card-outcome" />
                                                <span className="text-muted-foreground" data-testid="rental-application-card-age">{t('card.age', { count: c.age_days })}</span>
                                                <span className="text-muted-foreground">· {t(`source.${c.source}`, { defaultValue: c.source })}</span>
                                            </div>
                                        </Link>
                                    ))}
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>
            )}
            {!isLoading && <ConversionCard cards={cards} />}
            <ApplicationCreateDialog org={org} open={creating} onOpenChange={setCreating} listingUid={search.listing} />
        </div>
    )
}

type ListingOption = { uid: string; asset_name: string; status: string }

/** Enquiries no portal reference or address matched: a manager attaches them (spec 2026-10-25-12). */
function UnmatchedColumn({ org, cards, listings, canWrite }: { org: string; cards: ApplicationCard[]; listings: ListingOption[]; canWrite: boolean }) {
    const { t } = useTranslation('rentalApplications')
    const refresh = useRefreshApplications()
    const attach = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/attach-listing', { onSuccess: () => refresh() })
    return (
        <Card className="w-64 shrink-0 border-dashed" data-testid="rental-applications-unmatched-column">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                    {t('unmatched.title')} <span className="text-muted-foreground">({cards.length})</span>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {cards.map((c) => (
                    <div key={c.uid} className="space-y-1 rounded-md border p-2 text-sm" data-testid="rental-application-card" data-application-uid={c.uid}>
                        <Link to="/$org/rental-applications/$applicationUid" params={{ org, applicationUid: c.uid }} className="font-medium hover:underline">
                            {c.lead_name ?? '—'}
                        </Link>
                        <div className="text-xs text-muted-foreground">{t(`source.${c.source}`, { defaultValue: c.source })}</div>
                        {canWrite && (
                            <Select onValueChange={(v) => attach.mutate({ params: { path: { orgCode: org, applicationUid: c.uid } }, body: { listing_uid: v } })}>
                                <SelectTrigger className="h-8" data-testid="rental-application-attach-listing"><SelectValue placeholder={t('unmatched.attach')} /></SelectTrigger>
                                <SelectContent>
                                    {listings.filter((l) => l.status !== 'withdrawn' && l.status !== 'let').map((l) => (
                                        <SelectItem key={l.uid} value={l.uid} data-testid={`rental-application-attach-listing-${l.uid}`}>{l.asset_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    )
}

/** The conversion of the visible applications, by source and by outcome reason. */
function ConversionCard({ cards }: { cards: ApplicationCard[] }) {
    const { t } = useTranslation('rentalApplications')
    const bySource = new Map<string, { total: number; leased: number }>()
    const byReason = new Map<string, number>()
    for (const c of cards) {
        const s = bySource.get(c.source) ?? { total: 0, leased: 0 }
        s.total++
        if (c.stage === 'leased') s.leased++
        bySource.set(c.source, s)
        if (c.outcome) {
            const key = `${c.outcome}:${c.outcome_reason_code ?? ''}`
            byReason.set(key, (byReason.get(key) ?? 0) + 1)
        }
    }
    if (cards.length === 0) return null
    return (
        <Card data-testid="rental-applications-conversion">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('conversion.title')}</CardTitle></CardHeader>
            <CardContent className="grid gap-6 text-sm md:grid-cols-2">
                <div className="space-y-1">
                    <p className="font-medium">{t('conversion.by_source')}</p>
                    {[...bySource.entries()].map(([source, v]) => (
                        <p key={source} data-testid="rental-applications-conversion-source" data-source={source}>
                            {t(`source.${source}`, { defaultValue: source })}: {t('conversion.rate', { leased: v.leased, total: v.total, pct: Math.round((100 * v.leased) / v.total) })}
                        </p>
                    ))}
                </div>
                <div className="space-y-1">
                    <p className="font-medium">{t('conversion.by_reason')}</p>
                    {byReason.size === 0 && <p className="text-muted-foreground">{t('conversion.no_outcome')}</p>}
                    {[...byReason.entries()].map(([key, n]) => {
                        const [outcome, reason] = key.split(':')
                        return (
                            <p key={key} data-testid="rental-applications-conversion-reason">
                                {t(`outcome.${outcome}`, { defaultValue: outcome })}{reason ? ` · ${reason.replaceAll('_', ' ')}` : ''}: {n}
                            </p>
                        )
                    })}
                </div>
            </CardContent>
        </Card>
    )
}
